/** Format helpers used across the UI. Pure functions, no React. */

export function formatTokens(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

export function formatCost(usd: string | number): string {
  const n = typeof usd === "string" ? Number(usd) : usd;
  if (!Number.isFinite(n)) return "$0.00";
  // Clamp tiny negatives so we never render "$-0.00".
  return `$${Math.max(0, n).toFixed(2)}`;
}

export function elapsed(fromIso: string | null, toIso?: string | null): string {
  if (!fromIso) return "—";
  const start = Date.parse(fromIso);
  if (Number.isNaN(start)) return "—";
  const end = toIso ? Date.parse(toIso) : Date.now();
  const secs = Math.max(0, Math.floor((end - start) / 1000));
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m < 60) return `${m}:${String(s).padStart(2, "0")}`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/**
 * Stable hash of a project ID → small integer in [0, modulo).
 * Used to pick a monogram tint hue from a fixed palette.
 */
export function hashId(id: string, modulo: number): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % modulo;
}

/**
 * Compact relative time for thread lists and sidebars: "now", "12m", "1h",
 * "2d", then a short date once we cross a week.
 */
export function relativeShort(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const diff = Date.now() - t;
  const s = Math.floor(diff / 1000);
  if (s < 30) return "now";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(t).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/** Wall-clock time of an ISO timestamp, local, "HH:MM". */
export function formatClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
